// 1 Kyat = 16 Pal, 1 Pal = 7.5 Yway — the ratio actually used by every
// formatGold() call site (form inputs for Kyat/Pal/Yway weight fields).
const PAL_PER_KYAT = 16
const YWAY_PER_PAL = 7.5

// Coerce to a finite number, defaulting to 0 for null/undefined/''/NaN —
// mirrors every call site's `parseFloat(x) || 0` guard, but also catches
// non-numeric garbage (objects, symbols) that parseFloat(x) itself would
// throw on or silently mishandle.
function toFiniteNumber(value) {
  if (value == null || value === '') return 0
  const n = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * KPY (Kyat/Pal/Yway) form inputs → normalized decimal Kyat.
 *
 * Borrows/carries exactly one unit per step (single `if`, not a loop) —
 * matches the original formatGold() convention used across the codebase,
 * e.g. ProductMiscDetail.vue:
 *   if (yway < 0) { pal -= 1; yway += 7.5 }
 *   else if (yway >= 7.5) { const c = Math.trunc(yway / 7.5); pal += c; yway -= c * 7.5 }
 *   if (pal < 0) { kyat -= 1; pal += 16 }
 *   else if (pal >= 16) { const c = Math.trunc(pal / 16); kyat += c; pal -= c * 16 }
 * A shortfall spanning more than one Pal/Kyat unit (e.g. yway summing to
 * -20) is left only partially corrected — this is the original sites' own
 * latent behavior, not something introduced here.
 *
 * Never throws — invalid/non-numeric inputs are treated as 0.
 *
 * @param {{kyat?: number|string, pal?: number|string, yway?: number|string}} raw
 * @returns {{wt: number, wtStr: string, kyat: number, pal: number, yway: number}}
 *   wt/wtStr use the *normalized* kyat/pal/yway (also returned individually
 *   for callers that persist them, e.g. netK/netP/netY fields).
 */
export function kpyToKyat(raw) {
  let k = toFiniteNumber(raw?.kyat)
  let p = toFiniteNumber(raw?.pal)
  let y = toFiniteNumber(raw?.yway)

  if (y < 0) {
    p -= 1
    y += YWAY_PER_PAL
  } else if (y >= YWAY_PER_PAL) {
    const carry = Math.trunc(y / YWAY_PER_PAL)
    p += carry
    y -= carry * YWAY_PER_PAL
  }

  if (p < 0) {
    k -= 1
    p += PAL_PER_KYAT
  } else if (p >= PAL_PER_KYAT) {
    const carry = Math.trunc(p / PAL_PER_KYAT)
    k += carry
    p -= carry * PAL_PER_KYAT
  }

  y = Number(y.toFixed(2))

  return {
    wt: k + (p / PAL_PER_KYAT) + (y / (PAL_PER_KYAT * YWAY_PER_PAL)),
    wtStr: `${k || 0}-${p || 0}-${y || 0}`,
    kyat: k,
    pal: p,
    yway: y
  }
}

/**
 * Decimal Kyat → KPY (Kyat/Pal/Yway), the reverse of kpyToKyat()'s
 * conversion step. Matches the ad hoc fromKyat()/reformat logic in
 * OutReturnFormDialog.vue and GsFlowFormDialog.vue.
 *
 * Never throws — invalid/non-numeric input is treated as 0.
 *
 * @param {number} totalKyat
 * @param {{clampMin?: boolean}} [opts] clampMin: floor negative totals to 0
 *   before converting (OutReturnFormDialog.vue's fromKyat behavior).
 *   Defaults to false (GsFlowFormDialog.vue's reformat does not clamp).
 * @returns {{kyat: number, pal: number, yway: number}}
 */
export function kyatToKpy(totalKyat, opts) {
  let total = toFiniteNumber(totalKyat)
  if (opts?.clampMin && total < 0) total = 0

  const kyat = Math.floor(total)
  const remainingPal = (total - kyat) * PAL_PER_KYAT
  let pal = Math.floor(remainingPal)
  let yway = Number(((remainingPal - pal) * YWAY_PER_PAL).toFixed(2))

  // yway is mathematically < 7.5 before rounding, but toFixed(2) can round
  // a value like 7.499... up to exactly 7.50 — carry that into pal, same
  // as the backend's Java `if (yway.compareTo(eight) >= 0)` (eight = 7.5).
  if (yway >= YWAY_PER_PAL) {
    pal += 1
    yway = Number((yway - YWAY_PER_PAL).toFixed(2))
  }

  return { kyat, pal, yway }
}
